import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../api/config";

import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload } from 'lucide-react';

import { showSuccess, showError, showInfo, redirectPage } from "../utils/notification";
import { useAuth } from "../auth/AuthContext";
import { useLang } from "../i18n/LanguageContext";

// Location dropdowns are populated live from the tank master via the
// filter-options endpoint. The old hardcoded LOCATION_MASTER only had
// 7 districts — replaced with cascading API-driven dropdowns.

export default function RegisterPage() {
  // FRS Section I (1): Applicant Type — NGOs/Institution or Individual (non-organizational)
  // Applicant type values stay 'NGO' / 'Others' (backend user_type); the UI
  // shows them as "Organization" / "Individual (Non-Organizational)".
  const [userType, setUserType] = useState('NGO');
  const { tr } = useLang();
  const [formData, setFormData] = useState({
    email_id: '',
    password: '',
    full_name: '',
    mobile_number: '',
    pan_number: '',
    gst_number: '',
    contact_person_name: '',
    contact_person_email: '',
    // FRS additions
    founder_promoter_name: '', // mandatory for NGO/Institution
    proponent_name: '',        // mandatory for both — site coordinator
    aadhaar_number: '',        // mandatory for Individual (12 digits)
    address: '',
    district: '',
    taluk: '',
    block: '',
    village: '',
    pin_code: '',
  });

  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  // Location dropdowns — populated live from the public masters API.
  // District → Taluk only (Block / Village are not collected on registration).
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks]       = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/public/districts`, { silent: true })
      .then((r) => setDistricts(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setDistricts([]));
  }, []);

  useEffect(() => {
    if (!formData.district) { setTaluks([]); return; }
    axios.get(`${API_BASE_URL}/public/taluks`, { params: { district_name: formData.district }, silent: true })
      .then((r) => setTaluks(Array.isArray(r?.data?.data) ? r.data.data : []))
      .catch(() => setTaluks([]));
  }, [formData.district]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const [uploadedFiles, setUploadedFiles] = useState({
    registration_cretificate_path: null,
    pan_card_path: null,
    organization_profile_path: null,
    similar_project_details_path: null,
    resolution_copy_path: null,
  });

  const isNGO = userType === 'NGO';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (fileType, file) => {
    setUploadedFiles(prev => ({ ...prev, [fileType]: file }));
  };

  // Field mapping to the backend (columns unchanged):
  //   Organization — "Name of the Organization" → full_name,
  //                  "Authorized Person (with Designation)" → proponent_name
  //   Individual   — "Applicant Name" → full_name + proponent_name
  //   Individual Aadhaar Card Copy is uploaded in the resolution_copy_path slot.
  const validate = () => {
    if (!formData.email_id || !formData.password || !formData.full_name) {
      showError(tr('Please fill in all required fields.', 'அனைத்துக் கட்டாயப் புலங்களையும் நிரப்பவும்.'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email_id)) {
      showError(tr('Please enter a valid email address.', 'சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்.'));
      return false;
    }

    // Strong password policy (pen-test fix 5.3). Server re-validates.
    const pwd = formData.password || '';
    if (pwd.length < 8) { showError('Password must be at least 8 characters long.'); return false; }
    if (!/[a-z]/.test(pwd)) { showError('Password must contain at least one lowercase letter.'); return false; }
    if (!/[A-Z]/.test(pwd)) { showError('Password must contain at least one uppercase letter.'); return false; }
    if (!/[0-9]/.test(pwd)) { showError('Password must contain at least one digit.'); return false; }
    if (!/[^A-Za-z0-9]/.test(pwd)) { showError('Password must contain at least one special character.'); return false; }
    const localPart = String(formData.email_id).split('@')[0]?.toLowerCase() || '';
    if (localPart.length >= 3 && pwd.toLowerCase().includes(localPart)) {
      showError('Password must not contain your email address.');
      return false;
    }

    if (isNGO) {
      if (!formData.proponent_name) { showError(tr('Name of the Authorized Person is required.', 'அங்கீகரிக்கப்பட்ட நபரின் பெயர் தேவை.')); return false; }
      if (!formData.mobile_number) { showError(tr('Phone Number is required.', 'தொலைபேசி எண் தேவை.')); return false; }
      if (!formData.pan_number) { showError(tr('PAN Number is required for Organizations.', 'அமைப்புகளுக்குப் பான் எண் தேவை.')); return false; }
      if (!uploadedFiles.registration_cretificate_path) { showError(tr('Registration Certificate is required.', 'பதிவுச் சான்றிதழ் தேவை.')); return false; }
      if (!uploadedFiles.pan_card_path) { showError(tr('PAN Card Copy is required.', 'பான் அட்டை நகல் தேவை.')); return false; }
      if (!uploadedFiles.organization_profile_path) { showError(tr('Organization Profile is required.', 'அமைப்பின் விவரக்குறிப்பு தேவை.')); return false; }
    } else {
      if (!formData.mobile_number) { showError(tr('Phone Number is required.', 'தொலைபேசி எண் தேவை.')); return false; }
      if (!/^\d{12}$/.test(formData.aadhaar_number)) { showError(tr('Aadhaar Number of the Applicant must be 12 digits.', 'விண்ணப்பதாரரின் ஆதார் எண் 12 இலக்கங்களாக இருக்க வேண்டும்.')); return false; }
      if (!uploadedFiles.resolution_copy_path) { showError(tr('Aadhaar Card Copy is required.', 'ஆதார் அட்டை நகல் தேவை.')); return false; }
    }

    // Location: District, Taluk and PIN mandatory. Block and Village are not
    // collected on registration (client direction, Oct 2026).
    if (!formData.address) { showError(tr('Address for Communication is required.', 'தொடர்பு முகவரி தேவை.')); return false; }
    if (!formData.district) { showError(tr('District is required.', 'மாவட்டம் தேவை.')); return false; }
    if (!formData.taluk) { showError(tr('Taluk is required.', 'வட்டம் தேவை.')); return false; }
    if (!formData.pin_code || !/^\d{6}$/.test(formData.pin_code)) { showError(tr('PIN Code must be 6 digits.', 'அஞ்சல் குறியீடு 6 இலக்கங்களாக இருக்க வேண்டும்.')); return false; }

    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
      showError(tr('Please enter a valid PAN number.', 'சரியான பான் எண்ணை உள்ளிடவும்.'));
      return false;
    }
    if (formData.gst_number && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst_number)) {
      showError(tr('Please enter a valid GST number.', 'சரியான ஜிஎஸ்டி எண்ணை உள்ளிடவும்.'));
      return false;
    }
    if (formData.mobile_number && !/^\d{10}$/.test(formData.mobile_number)) {
      showError(tr('Please enter a valid 10-digit phone number.', 'சரியான 10 இலக்கத் தொலைபேசி எண்ணை உள்ளிடவும்.'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Derive the backend fields from what this applicant type shows.
    const payload = { ...formData };
    if (isNGO) {
      payload.founder_promoter_name = '';     // org name lives in full_name now
      payload.aadhaar_number = '';
    } else {
      payload.proponent_name = formData.full_name; // Applicant Name
      payload.founder_promoter_name = '';
    }
    payload.contact_person_name = '';
    payload.contact_person_email = '';

    const submitFormData = new FormData();
    submitFormData.append("user_type", userType);
    Object.keys(payload).forEach(key => {
      if (payload[key] !== '' && payload[key] !== null && payload[key] !== undefined) {
        submitFormData.append(key, payload[key]);
      }
    });
    const fileKeys = isNGO
      ? ['registration_cretificate_path', 'pan_card_path', 'organization_profile_path', 'similar_project_details_path']
      : ['resolution_copy_path', 'pan_card_path', 'similar_project_details_path'];
    fileKeys.forEach(fileKey => {
      if (uploadedFiles[fileKey]) submitFormData.append(fileKey, uploadedFiles[fileKey]);
    });

    axios.post(`${API_BASE_URL}/users`, submitFormData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
      .then(response => {
        if (response.data && response.data.success === true) {
          showSuccess(response.data.message || tr("Registration successful! Please log in.", "பதிவு வெற்றிகரமாக முடிந்தது! உள்நுழையவும்."));
          redirectPage("/login");
        } else {
          showError(response.data?.message || tr("Registration failed.", "பதிவு தோல்வியடைந்தது."));
        }
      })
      .catch(error => {
        showError(error?.response?.data?.message || tr("Registration failed. Please try again.", "பதிவு தோல்வியடைந்தது. மீண்டும் முயற்சிக்கவும்."));
      });
  };

  const handleBack = () => {
    window.location.href = '/login';
  };

  const inputCls = 'w-full px-3 py-3 text-sm border border-gray-300 shadow-sm focus:ring-2 focus:ring-brand-600 focus:border-brand-600 outline-none transition-all';
  const req = <span className="text-red-500">*</span>;
  const opt = <span className="text-gray-500">({tr('Optional', 'விருப்பத்தேர்வு')})</span>;
  const Label = ({ children }) => <label className="block text-sm font-medium text-gray-700 mb-2">{children}</label>;

  return (
    <div className="min-h-screen bg-slate-100 p-2 sm:p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="w-full px-3 sm:px-6 py-4 sm:py-5 bg-brand-800 border-b-4 border-brand-accent">
          <div className="flex items-center justify-between sm:justify-center relative">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-brand-100 hover:text-white transition-colors sm:absolute sm:left-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm font-medium hidden sm:inline">{tr('Back to Login', 'உள்நுழைவுக்குத் திரும்ப')}</span>
              <span className="text-xs font-medium sm:hidden">{tr('Back', 'திரும்ப')}</span>
            </button>

            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 flex-shrink-0">
                <img src="/images/tnlogo.png" alt="Tamil Nadu Government Logo" className="w-full h-full object-contain" />
              </div>
              <div className="text-left">
                <h1 className="text-sm sm:text-base lg:text-lg font-bold text-white leading-tight">ஏரி இணையதளம்</h1>
                <h2 className="text-xs sm:text-sm text-brand-100 font-medium leading-tight">{tr('Registration for NOC', 'தடையின்மைச் சான்றுக்கான பதிவு')}</h2>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm border border-slate-200 border-t-0 overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="space-y-8">

              {/* Basic Information */}
              <div>
                <div className="flex items-center mb-6 px-4 py-2.5 bg-sky-50 border-l-4 border-sky-500">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900">{tr('Basic Information', 'அடிப்படைத் தகவல்கள்')}</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <Label>{tr('Applicant Type', 'விண்ணப்பதாரர் வகை')} {req}</Label>
                    <select value={userType} onChange={(e) => setUserType(e.target.value)} className={inputCls}>
                      <option value="NGO">{tr('Organization', 'அமைப்பு')}</option>
                      <option value="Others">{tr('Individual (Non-Organizational)', 'தனிநபர் (அமைப்பு சாராதவர்)')}</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6">
                  {/* Name of the Organization / Applicant Name → full_name */}
                  <div>
                    <Label>{isNGO ? tr('Name of the Organization', 'அமைப்பின் பெயர்') : tr('Applicant Name', 'விண்ணப்பதாரர் பெயர்')} {req}</Label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      maxLength={isNGO ? 200 : 100}
                      className={inputCls}
                      placeholder={isNGO ? tr('Enter name of the organization', 'அமைப்பின் பெயரை உள்ளிடவும்') : tr('Enter applicant name', 'விண்ணப்பதாரர் பெயரை உள்ளிடவும்')}
                    />
                  </div>

                  {isNGO ? (
                    <div>
                      <Label>{tr('Name of the Authorized Person (with Designation)', 'அங்கீகரிக்கப்பட்ட நபரின் பெயர் (பதவியுடன்)')} {req}</Label>
                      <input
                        type="text"
                        value={formData.proponent_name}
                        onChange={(e) => handleInputChange('proponent_name', e.target.value)}
                        maxLength={100}
                        className={inputCls}
                        placeholder={tr('e.g. R. Kumar (Secretary)', 'எ.கா. இரா. குமார் (செயலாளர்)')}
                      />
                    </div>
                  ) : (
                    <div>
                      <Label>{tr('Aadhaar Number of the Applicant', 'விண்ணப்பதாரரின் ஆதார் எண்')} {req}</Label>
                      <input
                        type="text"
                        value={formData.aadhaar_number}
                        onChange={(e) => handleInputChange('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                        maxLength={12}
                        className={inputCls}
                        placeholder={tr('12 digit Aadhaar', '12 இலக்க ஆதார் எண்')}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
                  <div>
                    <Label>{tr('Email Address', 'மின்னஞ்சல் முகவரி')} {req}</Label>
                    <input type="email" value={formData.email_id} onChange={(e) => handleInputChange('email_id', e.target.value)}
                      className={inputCls} placeholder={tr('Enter email address', 'மின்னஞ்சல் முகவரியை உள்ளிடவும்')} />
                  </div>

                  <div>
                    <Label>{tr('Password', 'கடவுச்சொல்')} {req}</Label>
                    <input type="password" value={formData.password} onChange={(e) => handleInputChange('password', e.target.value)}
                      className={inputCls} placeholder={tr('Create password', 'கடவுச்சொல்லை உருவாக்கவும்')} minLength={8} maxLength={128} />
                    {/* Password-policy live hints (pen-test fix 5.3) */}
                    <ul className="mt-2 text-xs space-y-0.5">
                      {(() => {
                        const pwd = formData.password || '';
                        const rules = [
                          { ok: pwd.length >= 8, text: tr('At least 8 characters', 'குறைந்தது 8 எழுத்துகள்') },
                          { ok: /[a-z]/.test(pwd), text: tr('One lowercase letter', 'ஒரு சிறிய எழுத்து (a-z)') },
                          { ok: /[A-Z]/.test(pwd), text: tr('One uppercase letter', 'ஒரு பெரிய எழுத்து (A-Z)') },
                          { ok: /[0-9]/.test(pwd), text: tr('One digit', 'ஒரு எண்') },
                          { ok: /[^A-Za-z0-9]/.test(pwd), text: tr('One special character (e.g. @ # $ !)', 'ஒரு சிறப்பு எழுத்து (எ.கா. @ # $ !)') },
                        ];
                        return rules.map((r, i) => (
                          <li key={i} className={r.ok ? 'text-emerald-600' : 'text-gray-500'}>
                            {r.ok ? '✓' : '○'} {r.text}
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>

                  <div>
                    <Label>{tr('Phone Number', 'தொலைபேசி எண்')} {req}</Label>
                    <input type="tel" value={formData.mobile_number} onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                      className={inputCls} placeholder={tr('10-digit phone number', '10 இலக்கத் தொலைபேசி எண்')} />
                  </div>

                  <div>
                    <Label>{tr('PAN Number', 'பான் எண்')} {isNGO ? req : opt}</Label>
                    <input type="text" value={formData.pan_number} onChange={(e) => handleInputChange('pan_number', e.target.value.toUpperCase())}
                      className={inputCls} placeholder={tr('Enter PAN number', 'பான் எண்ணை உள்ளிடவும்')} />
                  </div>

                  <div>
                    <Label>{tr('GST Number', 'ஜிஎஸ்டி எண்')} <span className="text-gray-500">({tr('if applicable', 'பொருந்தினால்')})</span></Label>
                    <input type="text" value={formData.gst_number} onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase())}
                      className={inputCls} placeholder={tr('Enter GST number', 'ஜிஎஸ்டி எண்ணை உள்ளிடவும்')} />
                  </div>
                </div>
              </div>

              {/* Address & Location */}
              <div>
                <div className="flex items-center mb-6 px-4 py-2.5 bg-emerald-50 border-l-4 border-emerald-500">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900">{tr('Address & Location', 'முகவரி மற்றும் இருப்பிடம்')}</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Label>{tr('Address for Communication', 'தொடர்பு முகவரி')} {req}</Label>
                    <textarea value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)}
                      maxLength={100} rows={2} className={inputCls}
                      placeholder={tr('Door / Street / Area (max 100 chars)', 'கதவு எண் / தெரு / பகுதி (அதிகபட்சம் 100 எழுத்துகள்)')} />
                  </div>

                  <div>
                    <Label>{tr('District', 'மாவட்டம்')} {req}</Label>
                    <select
                      value={formData.district}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((p) => ({ ...p, district: v, taluk: '', block: '', village: '' }));
                      }}
                      className={inputCls}
                    >
                      <option value="">— {tr('Select District', 'மாவட்டத்தைத் தேர்வு செய்க')} —</option>
                      {districts.map((d) => (
                        <option key={d.district_id} value={d.district_name}>{d.district_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label>{tr('Taluk', 'வட்டம்')} {req}</Label>
                    <select value={formData.taluk} disabled={!formData.district}
                      onChange={(e) => setFormData((p) => ({ ...p, taluk: e.target.value, block: '', village: '' }))}
                      className={`${inputCls} disabled:bg-gray-100`}>
                      <option value="">— {tr('Select Taluk', 'வட்டத்தைத் தேர்வு செய்க')} —</option>
                      {taluks.map((t) => (<option key={t} value={t}>{t}</option>))}
                    </select>
                  </div>



                  <div>
                    <Label>{tr('PIN Code', 'அஞ்சல் குறியீடு')} {req}</Label>
                    <input type="text" value={formData.pin_code}
                      onChange={(e) => handleInputChange('pin_code', e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6} className={inputCls} placeholder={tr('6 digits', '6 இலக்கங்கள்')} />
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div>
                <div className="flex items-center mb-6 px-4 py-2.5 bg-amber-50 border-l-4 border-amber-500">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900">{tr('Document Upload', 'ஆவணப் பதிவேற்றம்')}</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {isNGO ? (
                    <>
                      <FileField id="registration_cretificate_path" label={tr('Registration Certificate', 'பதிவுச் சான்றிதழ்')} required tr={tr}
                        file={uploadedFiles.registration_cretificate_path} onChange={(f) => handleFileUpload('registration_cretificate_path', f)} />
                      <FileField id="pan_card_path" label={tr('PAN Card Copy', 'பான் அட்டை நகல்')} required tr={tr}
                        file={uploadedFiles.pan_card_path} onChange={(f) => handleFileUpload('pan_card_path', f)} />
                      <FileField id="organization_profile_path" label={tr('Organization Profile', 'அமைப்பின் விவரக்குறிப்பு')} required tr={tr}
                        file={uploadedFiles.organization_profile_path} onChange={(f) => handleFileUpload('organization_profile_path', f)} />
                      <FileField id="similar_project_details_path" label={tr('Similar Project Details', 'இதே போன்ற திட்ட விவரங்கள்')} tr={tr}
                        file={uploadedFiles.similar_project_details_path} onChange={(f) => handleFileUpload('similar_project_details_path', f)} />
                    </>
                  ) : (
                    <>
                      {/* Aadhaar Card Copy — stored in the resolution_copy_path slot */}
                      <FileField id="resolution_copy_path" label={tr('Aadhaar Card Copy', 'ஆதார் அட்டை நகல்')} required tr={tr}
                        file={uploadedFiles.resolution_copy_path} onChange={(f) => handleFileUpload('resolution_copy_path', f)} />
                      <FileField id="pan_card_path" label={tr('PAN Card Copy', 'பான் அட்டை நகல்')} tr={tr}
                        file={uploadedFiles.pan_card_path} onChange={(f) => handleFileUpload('pan_card_path', f)} />
                      <FileField id="similar_project_details_path" label={tr('Similar Project Details', 'இதே போன்ற திட்ட விவரங்கள்')} tr={tr}
                        file={uploadedFiles.similar_project_details_path} onChange={(f) => handleFileUpload('similar_project_details_path', f)} />
                    </>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-center pt-6 border-t border-gray-100">
                <button
                  onClick={handleSubmit}
                  type="submit"
                  className="bg-brand-700 text-white px-8 sm:px-12 py-3 font-semibold text-sm sm:text-base hover:bg-brand-800 transition-colors shadow-sm focus:outline-none focus:ring-4 focus:ring-brand-100"
                >
                  {tr('Submit Registration', 'பதிவைச் சமர்ப்பிக்கவும்')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FileField({ id, label, required, file, onChange, tr = (en) => en }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {label} {required ? <span className="text-red-500">*</span> : <span className="text-gray-500">({tr('Optional', 'விருப்பத்தேர்வு')})</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          id={id}
          className="hidden"
          accept=".jpg,.jpeg,.png"
          onChange={(e) => {
            const f = e.target.files[0];
            if (!f) return;
            // Backend accepts PNG / JPEG only (userUploadMiddleware).
            if (!/\.(jpe?g|png)$/i.test(f.name)) {
              alert(tr('Only JPG, JPEG or PNG files are accepted.', 'JPG, JPEG அல்லது PNG கோப்புகள் மட்டுமே ஏற்கப்படும்.'));
              e.target.value = '';
              return;
            }
            onChange(f);
          }}
        />
        <label
          htmlFor={id}
          className="flex flex-col items-center justify-center w-full h-28 sm:h-32 border-2 border-dashed border-gray-300 cursor-pointer bg-gray-50 hover:bg-brand-50 hover:border-brand-600 transition-all group"
        >
          <Upload className="w-6 h-6 text-gray-400 group-hover:text-brand-600 mb-2 transition-colors" />
          <span className="text-sm text-brand-700 font-medium truncate max-w-full px-2 text-center">
            {file ? file.name : tr('Choose file', 'கோப்பைத் தேர்வு செய்க')}
          </span>
          <span className={`text-xs mt-1 ${required ? 'text-red-500' : 'text-gray-500'}`}>
            {required ? tr('Required', 'கட்டாயம்') : tr('Optional', 'விருப்பத்தேர்வு')}
          </span>
        </label>
      </div>
      <p className="mt-1.5 text-xs text-gray-500">
        {tr('Accepted file types: JPG, JPEG, PNG (max 15 MB)', 'ஏற்கப்படும் கோப்பு வகைகள்: JPG, JPEG, PNG (அதிகபட்சம் 15 MB)')}
      </p>
    </div>
  );
}
